// frontend/src/pages/Inventory.js
import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import ImageLightbox, { parseImageList, resizeEbayUrl } from '../components/ImageLightbox';

// ── eBay condition options ────────────────────────────────────────────────────
const CONDITIONS = [
  { id: '1000', name: 'New' },
  { id: '1500', name: 'New Other' },
  { id: '2000', name: 'Manufacturer Refurbished' },
  { id: '2500', name: 'Seller Refurbished' },
  { id: '2750', name: 'Like New' },
  { id: '3000', name: 'Used - Very Good' },
  { id: '4000', name: 'Used - Good' },
  { id: '5000', name: 'Used - Acceptable' },
  { id: '7000', name: 'For Parts or Not Working' },
];

function LocationEditor({ sku, current, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue]     = useState(current || '');
  const [saving, setSaving]   = useState(false);

  async function save() {
    if (!value.trim()) return;
    setSaving(true);
    await api.patch(`/products/${encodeURIComponent(sku)}/location`, { location: value.trim() });
    setSaving(false); setEditing(false);
    onSaved(value.trim().toUpperCase());
  }

  if (editing) return (
    <span style={{ display:'inline-flex', gap:4, alignItems:'center' }}>
      <input value={value} onChange={e => setValue(e.target.value.toUpperCase())}
        onKeyDown={e => { if (e.key==='Enter') save(); if (e.key==='Escape') setEditing(false); }}
        style={{ width:72, padding:'2px 6px', fontSize:11, fontFamily:'monospace' }} autoFocus />
      <button className="btn btn-success" style={{ padding:'2px 7px', fontSize:11 }} onClick={save} disabled={saving}>{saving?'…':'✓'}</button>
      <button className="btn btn-ghost" style={{ padding:'2px 6px', fontSize:11 }} onClick={() => setEditing(false)}>✕</button>
    </span>
  );
  return (
    <span onClick={() => setEditing(true)} title="Click to edit bin location"
      style={{ cursor:'pointer', display:'inline-flex', alignItems:'center', gap:4 }}>
      <span style={{ background:'rgba(108,99,255,.15)', color:'var(--accent)', padding:'2px 8px', borderRadius:6, fontFamily:'monospace', fontSize:11, fontWeight:600 }}>
        📦 {current || '—'}
      </span>
      <span style={{ fontSize:9, color:'var(--muted)' }}>✏️</span>
    </span>
  );
}

// ── Rich text editor using contentEditable ────────────────────────────────────
function RichEditor({ value, onChange, loading }) {
  const ref = useRef(null);
  const isFirst = useRef(true);

  useEffect(() => {
    if (ref.current && isFirst.current && value) {
      // Decode HTML entities and set as innerHTML so it renders properly
      const decoded = value.replace(/&lt;/g,'<').replace(/&gt;/g,'>').replace(/&amp;/g,'&').replace(/&quot;/g,'"').replace(/&#39;/g,"'");
      ref.current.innerHTML = decoded;
      isFirst.current = false;
    }
  }, [value]);

  function exec(cmd, val = null) {
    document.execCommand(cmd, false, val);
    ref.current?.focus();
  }

  if (loading) return (
    <div style={{ minHeight:180, background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8,
      padding:12, fontSize:12, color:'var(--muted)', display:'flex', alignItems:'center', justifyContent:'center' }}>
      ⏳ Loading description from eBay…
    </div>
  );

  return (
    <div style={{ border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
      {/* Toolbar */}
      <div style={{ display:'flex', gap:2, padding:'6px 8px', background:'rgba(255,255,255,.04)',
        borderBottom:'1px solid var(--border)', flexWrap:'wrap' }}>
        {[
          ['Bold',      'B', 'bold'],
          ['Italic',    'I', 'italic'],
          ['Underline', 'U', 'underline'],
        ].map(([title, label, cmd]) => (
          <button key={cmd} title={title} onMouseDown={e => { e.preventDefault(); exec(cmd); }}
            style={{ padding:'2px 8px', borderRadius:4, border:'1px solid var(--border)', background:'transparent',
              color:'var(--text)', cursor:'pointer', fontWeight: cmd==='bold'?700:cmd==='italic'?'italic':400,
              fontStyle: cmd==='italic'?'italic':'normal', textDecoration: cmd==='underline'?'underline':'none', fontSize:12 }}>
            {label}
          </button>
        ))}
        <div style={{ width:1, background:'var(--border)', margin:'0 4px' }} />
        <button title="Bullet list" onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }}
          style={{ padding:'2px 8px', borderRadius:4, border:'1px solid var(--border)', background:'transparent', color:'var(--text)', cursor:'pointer', fontSize:12 }}>
          • List
        </button>
        <button title="Clear formatting" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}
          style={{ padding:'2px 8px', borderRadius:4, border:'1px solid var(--border)', background:'transparent', color:'var(--muted)', cursor:'pointer', fontSize:11 }}>
          Clear
        </button>
      </div>
      {/* Editor */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current?.innerHTML || '')}
        style={{ minHeight:180, padding:'12px', outline:'none', fontSize:13,
          color:'var(--text)', background:'var(--bg)', lineHeight:1.6 }}
      />
    </div>
  );
}

// ── Full Edit Panel ───────────────────────────────────────────────────────────
function EditPanel({ product, onSaved, onClose, onFieldPatch }) {
  const [title, setTitle]       = useState(product.title || '');
  const [price, setPrice]       = useState(product.price || '');
  const [quantity, setQuantity] = useState(product.quantity ?? 0);
  const [description, setDesc]  = useState('');
  // Seed condition from stored condition name -> map to ID
  const getConditionId = (name) => {
    if (!name) return '';
    const match = CONDITIONS.find(c => c.name.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(c.name.toLowerCase()));
    return match ? match.id : '';
  };
  const [conditionId, setCondition] = useState(getConditionId(product.condition) || '');
  const [itemSpecifics, setSpecifics]     = useState([]);
  const [shippingProfileId, setShipping]  = useState('');
  const [returnProfileId,  setReturn]     = useState('');
  const [paymentProfileId, setPayment]    = useState('');
  const [policies, setPolicies]           = useState({ shipping:[], returns:[], payment:[] });
  const [loadingDesc, setLoadingDesc]     = useState(true);
  const [location, setLocation]           = useState(product.location || product.sku_bin || '');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [relisting, setRelisting] = useState(false);
  const [sellLocal, setSellLocal]   = useState(!!product.sell_local);
  const [sellingLocal, setSelling]  = useState(false);
  const ebaySrc = (product.channels || []).find(ch => ch.channel === 'ebay_source');
  const isSourceEnded = ebaySrc?.status === 'ended';

  // Unsaved-changes guard. Click-outside or Cancel/X prompts to save when dirty.
  const dirtyRef = useRef(false);
  const downOnBackdrop = useRef(false);
  const markDirty = () => { dirtyRef.current = true; };
  function requestClose() {
    if (saving) return;
    if (dirtyRef.current) {
      const ok = window.confirm('You have unsaved changes.\n\nClick OK to save, or Cancel to keep editing.');
      if (ok) { save(); }
      return;
    }
    onClose();
  }

  async function relistSource() {
    if (!window.confirm('Relist this item on eBay Euphoric? This will reactivate the ended listing.')) return;
    setRelisting(true); setError('');
    try {
      const r = await api.post(`/archive/${encodeURIComponent(product.sku)}/relist`);
      const updatedChannels = (product.channels || []).map(ch =>
        ch.channel === 'ebay_source' ? { ...ch, status: 'active' } : ch);
      onSaved({
        ...product,
        oldSku: product.sku,
        sku: r.data?.sku || product.sku,
        sku_bin: r.data?.sku_bin || product.sku_bin,
        sku_year: r.data?.sku_year || product.sku_year,
        quantity: r.data?.quantity ?? Math.max(1, product.quantity || 0),
        channels: updatedChannels,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Relist failed');
      setRelisting(false);
    }
  }

  // Pull photos/title/condition from the live Euphoric eBay listing (edits made on
  // eBay directly). Qty/price are master truth and are not touched.
  const [refreshing, setRefreshing] = useState(false);
  async function refreshFromEbay() {
    setRefreshing(true); setError('');
    try {
      const r = await api.post(`/products/${encodeURIComponent(product.sku)}/refresh-from-ebay`);
      alert(`Pulled ${r.data.images} photo(s)${r.data.tnt?.revised ? ' — TNT pictures updated too' : ''}.`);
      onSaved({ ...product, title: r.data.title || product.title, condition: r.data.condition || product.condition });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Refresh failed');
    } finally { setRefreshing(false); }
  }

  // End & Relist on all stores. For bin-found / mis-listed items (e.g. only on TNT):
  // confirm the bin location, then recreate a clean Euphoric listing (cloned from the
  // existing eBay listing) + mirror to TNT. New SKU = LOCATION_UPC_<currentYear>.
  async function endRelistAll() {
    const loc = window.prompt('Confirm the bin/location, then OK to END & RELIST this item on all stores:', location || product.location || product.sku_bin || '');
    if (loc === null) return;
    if (!loc.trim()) { setError('Location is required to relist'); return; }
    setRelisting(true); setError('');
    try {
      const r = await api.post(`/products/${encodeURIComponent(product.sku)}/end-relist`, { location: loc.trim() });
      alert(`Relisted as ${r.data.sku} on Euphoric${r.data.mirrored ? ' + TNT' : ''} (location ${r.data.location}).`);
      onSaved({ ...product, oldSku: product.sku, sku: r.data.sku || product.sku,
        sku_bin: (r.data.sku || '').split('_')[0] || product.sku_bin,
        location: r.data.location || loc.trim().toUpperCase() });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'End & Relist failed');
      setRelisting(false);
    }
  }

  // Local-pickup flag. Saves on its own endpoint the moment it's ticked — it does not
  // ride along with Save, because Save fires a ReviseItem against eBay and a pickup
  // checkbox has nothing to do with the eBay listing.
  async function toggleSellLocal(next) {
    setSellLocal(next);            // optimistic — reverted below if the call fails
    setError('');
    try {
      await api.patch(`/products/${encodeURIComponent(product.sku)}/sell-local`, { sellLocal: next });
      onFieldPatch?.(product.sku, { sell_local: next ? 1 : 0 });
    } catch (err) {
      setSellLocal(!next);
      setError(err.response?.data?.error || 'Could not save the local-pickup flag');
    }
  }

  // Facebook Marketplace sales settle over Messenger and never reach syncOrders, so the
  // master decrement has to be recorded by hand. Master is the source of truth; the
  // backend pushes the new qty out to eBay / TNT / Shopify from there.
  async function markSoldLocally() {
    const current = parseInt(quantity) || 0;
    if (current <= 0) { setError('Quantity is already 0'); return; }
    if (!window.confirm(
      `Mark 1 sold locally?\n\n${product.sku}\nQuantity ${current} → ${current - 1}\n\n` +
      `This drops the quantity on eBay, TNT and Shopify too.`)) return;

    setSelling(true); setError('');
    try {
      const r = await api.post(`/products/${encodeURIComponent(product.sku)}/sold-local`, { quantity: 1 });
      setQuantity(r.data.newQty);
      onFieldPatch?.(product.sku, { quantity: r.data.newQty });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record the local sale');
    } finally {
      setSelling(false);
    }
  }

  useEffect(() => {
    // Fetch full item details
    api.get(`/products/${encodeURIComponent(product.sku)}/details`)
      .then(r => {
        if (r.data.title)           setTitle(r.data.title);
        if (r.data.price)           setPrice(r.data.price);
        if (r.data.description)     setDesc(r.data.description);
        if (r.data.conditionId)     setCondition(r.data.conditionId);
        if (r.data.location !== undefined) setLocation(r.data.location || '');
        if (r.data.itemSpecifics)   setSpecifics(r.data.itemSpecifics);
        if (r.data.shippingProfileId) setShipping(r.data.shippingProfileId);
        if (r.data.returnProfileId)   setReturn(r.data.returnProfileId);
        if (r.data.paymentProfileId)  setPayment(r.data.paymentProfileId);
      })
      .catch(() => {})
      .finally(() => setLoadingDesc(false));

    // Fetch policies
    api.get('/ebay/policies')
      .then(r => setPolicies(r.data))
      .catch(() => {});
  }, [product.sku]);

  async function save() {
    if (!title.trim()) return setError('Title is required');
    setSaving(true); setError('');
    try {
      // Read description directly from the contentEditable DOM in case state is stale
      const descEl = document.querySelector('[contenteditable="true"]');
      const currentDesc = descEl ? descEl.innerHTML : description;

      await api.patch(`/products/${encodeURIComponent(product.sku)}/edit`, {
        title: title.trim(),
        price: parseFloat(price) || 0,
        quantity: parseInt(quantity) || 0,
        description: currentDesc,
        location,
        conditionId,
        shippingProfileId,
        returnProfileId,
        paymentProfileId,
        itemSpecifics,
      });
      dirtyRef.current = false;
      setSuccess(true);
      setTimeout(() => onSaved({ ...product, title, price: parseFloat(price), quantity: parseInt(quantity), description: currentDesc, condition: conditionId, location: location.trim() ? location.trim().toUpperCase() : product.location }), 1200);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired — please log in again');
        setTimeout(() => { window.location.href = '/login'; }, 1500);
      } else {
        setError(err.response?.data?.error || 'Save failed');
      }
      setSaving(false);
    }
  }

  function updateSpecific(i, field, val) {
    markDirty();
    setSpecifics(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));
  }
  function addSpecific()    { markDirty(); setSpecifics(prev => [...prev, { name: '', value: '' }]); }
  function removeSpecific(i){ markDirty(); setSpecifics(prev => prev.filter((_, idx) => idx !== i)); }

  const imgList = parseImageList(product.images);
  const src = imgList[0] ? resizeEbayUrl(imgList[0], 500) : null;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(0);

  const [history, setHistory] = useState(null);

  const TABS = [
    { id: 'details',   label: 'Details'   },
    { id: 'photos',    label: `Photos${imgList.length > 0 ? ` (${imgList.length})` : ''}` },
    { id: 'pricing',   label: 'Pricing'   },
    { id: 'specifics', label: 'Specifics' },
    { id: 'shipping',  label: 'Policies'  },
    { id: 'history',   label: 'History'   },
  ];

  return (
    <>
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:2000, display:'flex', justifyContent:'flex-end' }}
      onMouseDown={e => { downOnBackdrop.current = (e.target === e.currentTarget); }}
      onClick={e => {
        if (e.target !== e.currentTarget) return;
        if (!downOnBackdrop.current) return;  // ignore drag-select that ended on backdrop
        requestClose();
      }}>
      <div style={{ width:'100%', maxWidth:520, background:'var(--surface)', height:'100%',
        overflowY:'auto', borderLeft:'1px solid var(--border)', display:'flex', flexDirection:'column' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
          padding:'14px 20px', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          <div style={{ fontWeight:700, fontSize:16 }}>✏️ Edit Listing</div>
          <button className="btn btn-ghost" style={{ padding:'4px 10px' }} onClick={requestClose}>✕</button>
        </div>

        {/* Item preview */}
        <div style={{ display:'flex', gap:12, padding:'12px 20px', borderBottom:'1px solid var(--border)',
          background:'rgba(255,255,255,.02)', flexShrink:0 }}>
          {src && (
            <div style={{ position:'relative', flexShrink:0, cursor:'zoom-in' }} onClick={() => setLightboxOpen(true)}>
              <img src={src} alt="" style={{ width:48, height:48, objectFit:'cover', borderRadius:8, border:'1px solid var(--border)', display:'block' }} />
              {imgList.length > 1 && (
                <div style={{ position:'absolute', bottom:-4, right:-4, background:'var(--accent)', color:'#fff', fontSize:10, fontWeight:700, padding:'2px 5px', borderRadius:10, lineHeight:1, border:'2px solid var(--surface)' }}>
                  +{imgList.length - 1}
                </div>
              )}
            </div>
          )}
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:12, fontFamily:'monospace', color:'var(--accent2)', marginBottom:2 }}>{product.sku}</div>
            <div style={{ fontSize:11, color:'var(--muted)' }}>
              {product.sku_upc  && <span>UPC: {product.sku_upc} · </span>}
              {product.sku_year && <span>Listed: {product.sku_year}</span>}
            </div>
            {(() => {
              // Per-channel "View on X" links built from each channel's stored URL.
              const VIEW_LABELS = { ebay_source: 'eBay', ebay_store2: 'TNT', walmart: 'Walmart', amazon: 'Amazon' };
              const order = { ebay_source: 0, ebay_store2: 1, walmart: 2, amazon: 3 };
              const links = (product.channels || [])
                .filter(ch => ch.status !== 'unlinked' && VIEW_LABELS[ch.channel])
                .map(ch => {
                  const url = ch.channel_url
                    || (((ch.channel === 'ebay_source' || ch.channel === 'ebay_store2') && ch.channel_item_id)
                          ? `https://www.ebay.com/itm/${ch.channel_item_id}` : null);
                  return url ? { channel: ch.channel, label: VIEW_LABELS[ch.channel], url } : null;
                })
                .filter(Boolean)
                .sort((a, b) => (order[a.channel] ?? 9) - (order[b.channel] ?? 9));
              // Fallback to the legacy top-level eBay link if channels aren't populated
              if (!links.length && (product.channel_item_id || product.channel_url)) {
                links.push({ channel: 'ebay_source', label: 'eBay',
                  url: product.channel_item_id ? `https://www.ebay.com/itm/${product.channel_item_id}` : product.channel_url });
              }
              return links.length ? (
                <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginTop:3 }}>
                  {links.map(l => (
                    <a key={l.channel} href={l.url} target="_blank" rel="noreferrer"
                      style={{ fontSize:11, color:'var(--accent)', display:'inline-block' }}>
                      View on {l.label} →
                    </a>
                  ))}
                </div>
              ) : null;
            })()}
            {isSourceEnded && (
              <div style={{ marginTop:6 }}>
                <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4, fontWeight:700,
                  background:'rgba(255,77,109,.15)', color:'var(--danger)', marginRight:8 }}>EBAY ENDED</span>
                <button onClick={relistSource} disabled={relisting}
                  style={{ fontSize:11, padding:'4px 10px', borderRadius:6, fontWeight:600,
                    border:'1px solid var(--accent)', background:'rgba(108,99,255,.1)',
                    color:'var(--accent)', cursor: relisting ? 'wait' : 'pointer' }}>
                  {relisting ? 'Relisting…' : '🔁 Relist on eBay'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid var(--border)', flexShrink:0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ flex:1, padding:'10px 4px', border:'none', background:'transparent', cursor:'pointer', fontSize:12, fontWeight:600,
                color: activeTab===t.id ? 'var(--accent)' : 'var(--muted)',
                borderBottom: activeTab===t.id ? '2px solid var(--accent)' : '2px solid transparent' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Form */}
        <div style={{ padding:'16px 20px', flex:1, display:'flex', flexDirection:'column', gap:14, overflowY:'auto' }}>

          {/* DETAILS TAB */}
          {activeTab === 'details' && <>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Title <span style={{ color:'var(--muted)', fontWeight:400 }}>({title.length}/80)</span></label>
              <input value={title} onChange={e => { markDirty(); setTitle(e.target.value); }} maxLength={80} placeholder="Item title" />
            </div>

            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Condition</label>
              <select value={conditionId} onChange={e => { markDirty(); setCondition(e.target.value); }}>
                <option value="">— Select condition —</option>
                {CONDITIONS.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Location / Bin</label>
              <input value={location} onChange={e => { markDirty(); setLocation(e.target.value.toUpperCase()); }}
                placeholder="e.g. A12" style={{ fontFamily:'monospace' }} />
              <div style={{ fontSize:10, color:'var(--muted)', marginTop:3 }}>
                Current physical bin — updates inventory location without changing SKU
              </div>
            </div>

            {/* Local pickup — bulky / expensive-to-ship items also offered on Facebook
                Marketplace. eBay stays live and wins any race; a local buyer who loses
                is just told it already sold. */}
            <div className="form-group" style={{ marginBottom:0 }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                <input type="checkbox" checked={sellLocal}
                  onChange={e => toggleSellLocal(e.target.checked)}
                  style={{ width:16, height:16, cursor:'pointer', margin:0 }} />
                <span>📍 Offer for local pickup</span>
              </label>
              <div style={{ fontSize:10, color:'var(--muted)', marginTop:3 }}>
                Bulky or expensive to ship — saves this SKU to the Facebook Marketplace list. Saves immediately.
              </div>

              {sellLocal && (
                <div style={{ marginTop:10, padding:'10px 12px', borderRadius:8,
                  border:'1px solid var(--border)', background:'rgba(255,255,255,.02)' }}>
                  {/* Set by the browser extension after Publish — gives us a way back to
                      the FB listing to pull it down when the item sells elsewhere. */}
                  <div style={{ fontSize:11, marginBottom:8 }}>
                    {product.fb_listed_at ? (
                      <span style={{ color:'var(--success, #4ade80)' }}>
                        ✓ Listed on Facebook {String(product.fb_listed_at).slice(0, 10)}
                        {product.fb_url && (
                          <a href={product.fb_url} target="_blank" rel="noreferrer"
                            style={{ color:'var(--accent)', marginLeft:8 }}>View →</a>
                        )}
                      </span>
                    ) : (
                      <span style={{ color:'var(--muted)' }}>Not yet listed on Facebook</span>
                    )}
                  </div>
                  <button onClick={markSoldLocally} disabled={sellingLocal || (parseInt(quantity) || 0) <= 0}
                    style={{ fontSize:12, padding:'6px 12px', borderRadius:6, fontWeight:600,
                      border:'1px solid var(--accent)', background:'rgba(108,99,255,.1)', color:'var(--accent)',
                      cursor: sellingLocal ? 'wait' : ((parseInt(quantity) || 0) <= 0 ? 'not-allowed' : 'pointer'),
                      opacity: (parseInt(quantity) || 0) <= 0 ? .5 : 1 }}>
                    {sellingLocal ? 'Recording…' : '🤝 Mark 1 sold locally'}
                  </button>
                  <div style={{ fontSize:10, color:'var(--muted)', marginTop:6 }}>
                    Facebook sales don't come through order sync. This decrements master and
                    pushes the new quantity to eBay, TNT and Shopify.
                  </div>
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Description</label>
              <RichEditor value={description} onChange={v => { markDirty(); setDesc(v); }} loading={loadingDesc} />
            </div>
          </>}

          {/* PHOTOS TAB */}
          {activeTab === 'photos' && (
            imgList.length === 0 ? (
              <div style={{ padding:24, textAlign:'center', color:'var(--muted)', fontSize:13 }}>
                No photos yet. New listings populate on the next sync; existing items fill in during the image backfill.
              </div>
            ) : (
              <div>
                <div style={{ fontSize:11, color:'var(--muted)', marginBottom:10 }}>
                  Click any photo to view full size. {imgList.length} photo{imgList.length === 1 ? '' : 's'} from eBay.
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:8 }}>
                  {imgList.map((url, i) => (
                    <img
                      key={i}
                      src={resizeEbayUrl(url, 300)}
                      alt=""
                      onClick={() => { setLightboxStart(i); setLightboxOpen(true); }}
                      style={{ width:'100%', aspectRatio:1, objectFit:'cover', borderRadius:6,
                        border:'1px solid var(--border)', cursor:'zoom-in', display:'block' }}
                    />
                  ))}
                </div>
              </div>
            )
          )}

          {/* PRICING TAB */}
          {activeTab === 'pricing' && <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Price ($)</label>
                <input type="number" value={price} onChange={e => { markDirty(); setPrice(e.target.value); }} placeholder="0.00" min="0" step="0.01" />
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Quantity</label>
                <input type="number" value={quantity} onChange={e => { markDirty(); setQuantity(e.target.value); }} placeholder="0" min="0" step="1" />
              </div>
            </div>
            <div style={{ background:'rgba(255,255,255,.03)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', fontSize:12, color:'var(--muted)' }}>
              Changes push to eBay Euphoric immediately. Next sync will update all other channels.
            </div>
          </>}

          {/* ITEM SPECIFICS TAB */}
          {activeTab === 'specifics' && <>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <label style={{ marginBottom:0 }}>Item Specifics</label>
              <button className="btn btn-ghost" style={{ fontSize:12, padding:'4px 10px' }} onClick={addSpecific}>+ Add</button>
            </div>
            {itemSpecifics.length === 0 ? (
              <div style={{ textAlign:'center', padding:'24px', color:'var(--muted)', fontSize:13 }}>
                No item specifics loaded yet — they'll appear after the item details load.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {itemSpecifics.map((s, i) => (
                  <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 32px', gap:8, alignItems:'center' }}>
                    <input value={s.name} onChange={e => updateSpecific(i, 'name', e.target.value)}
                      placeholder="Name (e.g. Color)" style={{ fontSize:12 }} />
                    <input value={s.value} onChange={e => updateSpecific(i, 'value', e.target.value)}
                      placeholder="Value (e.g. Black)" style={{ fontSize:12 }} />
                    <button className="btn btn-ghost" style={{ padding:'4px 6px', fontSize:11, color:'var(--danger)' }}
                      onClick={() => removeSpecific(i)}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </>}

          {/* POLICIES TAB */}
          {activeTab === 'shipping' && <>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Shipping Policy</label>
              <select value={shippingProfileId} onChange={e => { markDirty(); setShipping(e.target.value); }}>
                <option value="">— Keep current —</option>
                {policies.shipping.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Return Policy</label>
              <select value={returnProfileId} onChange={e => { markDirty(); setReturn(e.target.value); }}>
                <option value="">— Keep current —</option>
                {policies.returns.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom:0 }}>
              <label>Payment Policy</label>
              <select value={paymentProfileId} onChange={e => { markDirty(); setPayment(e.target.value); }}>
                <option value="">— Keep current —</option>
                {policies.payment.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          </>}

          {/* HISTORY TAB */}
          {activeTab === 'history' && (() => {
            // Load history on first view
            if (history === null) {
              api.get(`/products/${encodeURIComponent(product.sku)}/history`).then(r => setHistory(r.data.timeline || [])).catch(() => setHistory([]));
              return <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>Loading history...</div>;
            }
            if (history.length === 0) {
              return <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>No history recorded yet</div>;
            }

            const REASON_LABELS = {
              sale: 'Sale', edit: 'Edit', manual: 'Manual', manual_adjust: 'Manual Adjust',
              price_edit: 'Price Edit', return: 'Return', relist: 'Relist', ai_relist: 'AI Relist',
              restock: 'Restock',
            };
            const REASON_COLORS = {
              sale: 'var(--danger)', edit: 'var(--accent)', manual: 'var(--accent)',
              manual_adjust: 'var(--accent)', price_edit: 'var(--warn)', return: 'var(--accent2)',
              relist: 'var(--accent)', ai_relist: 'var(--accent)', restock: 'var(--accent2)',
            };
            const FIELD_LABELS = {
              quantity: 'Qty', price: 'Price', title: 'Title', description: 'Description',
              location: 'Location', condition: 'Condition', sku: 'SKU', status: 'Status',
            };

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {history.map((h, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0',
                    borderBottom: i < history.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    {/* Timeline dot */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16, flexShrink: 0, paddingTop: 4 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%',
                        background: REASON_COLORS[h.reason] || 'var(--muted)' }} />
                      {i < history.length - 1 && <div style={{ width: 1, flex: 1, background: 'var(--border)', marginTop: 4 }} />}
                    </div>
                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                        <span style={{ fontSize: 12, fontWeight: 700,
                          color: REASON_COLORS[h.reason] || 'var(--muted)' }}>
                          {REASON_LABELS[h.reason] || h.reason || 'Change'}
                        </span>
                        <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                          {h.date ? new Date(h.date).toLocaleString() : ''}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text)' }}>
                        <span style={{ color: 'var(--muted)' }}>{FIELD_LABELS[h.field] || h.field}: </span>
                        {h.oldValue && h.oldValue !== '(updated)' && (
                          <><span style={{ textDecoration: 'line-through', color: 'var(--muted)' }}>{h.field === 'price' ? `$${h.oldValue}` : h.oldValue}</span> → </>
                        )}
                        <span style={{ fontWeight: 600 }}>{h.field === 'price' ? `$${h.newValue}` : h.newValue}</span>
                      </div>
                      {h.channel && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>{h.channel}</div>}
                      {h.buyer && <div style={{ fontSize: 10, color: 'var(--muted)' }}>Buyer: {h.buyer}</div>}
                      {h.note && <div style={{ fontSize: 10, color: 'var(--muted)' }}>{h.note}</div>}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {error   && <div className="error-text">{error}</div>}
          {success && <div className="success-text">Saved and pushed to eBay</div>}
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 20px', borderTop:'1px solid var(--border)', display:'flex', gap:8, flexShrink:0, flexWrap:'wrap' }}>
          <button className="btn btn-primary" style={{ flex:1, padding:'12px 16px', fontSize:14 }}
            onClick={e => { e.preventDefault(); e.stopPropagation(); save(); }}
            disabled={saving || success}>
            {saving ? 'Saving...' : success ? 'Done' : 'Save & Push to eBay'}
          </button>
          <button className="btn btn-ghost" style={{ padding:'12px 16px', fontSize:14 }} onClick={requestClose}>Cancel</button>
          <button className="btn btn-ghost" style={{ padding:'12px 16px', fontSize:14, width:'100%', marginTop:8 }}
            disabled={saving || relisting || refreshing}
            onClick={e => { e.preventDefault(); e.stopPropagation(); refreshFromEbay(); }}
            title="Pull the current photos, title and condition from the live Euphoric eBay listing into Sync (and onto TNT if mirrored). Qty and price are not changed.">
            {refreshing ? 'Pulling from eBay…' : '⬇ Refresh photos from eBay'}
          </button>
          <button className="btn btn-ghost" style={{ padding:'12px 16px', fontSize:14, color:'var(--accent)', borderColor:'var(--accent)', width:'100%', marginTop:8 }}
            disabled={saving || relisting}
            onClick={e => { e.preventDefault(); e.stopPropagation(); endRelistAll(); }}
            title="End the current listing(s), confirm location, and relist cleanly on Euphoric + TNT">
            {relisting ? 'Relisting…' : '↻ End & Relist (all stores)'}
          </button>
          <button className="btn btn-ghost" style={{ padding:'12px 16px', fontSize:14, color:'var(--danger)', borderColor:'var(--danger)', width:'100%', marginTop:8 }}
            disabled={saving}
            onClick={async (e) => {
              e.preventDefault(); e.stopPropagation();
              if (!window.confirm(`End listing for ${product.sku} on EO, TNT, and Walmart?\n\nThis retires the listing on all channels but keeps the product record and history. Qty will be set to 0.`)) return;
              setSaving(true); setError('');
              try {
                const res = await api.post(`/products/${encodeURIComponent(product.sku)}/end-all`);
                const parts = Object.entries(res.data.results || {})
                  .map(([ch, r]) => `${ch}: ${r || 'n/a'}`).join('\n');
                alert('Ended:\n' + parts);
                onSaved({ ...product, quantity: 0 });
              } catch (err) {
                setError(err.response?.data?.error || 'End failed');
                setSaving(false);
              }
            }}>
            End Listing (All Channels)
          </button>
        </div>
      </div>
    </div>
    {lightboxOpen && <ImageLightbox images={imgList} startIndex={lightboxStart} onClose={() => setLightboxOpen(false)} />}
    </>
  );
}

// ── Item Row ──────────────────────────────────────────────────────────────────
const CHANNEL_LABELS = { walmart: 'Walmart', ebay_store2: 'TNT Operations', amazon: 'Amazon' };

function ChannelModal({ sku, channels, onUpdate, onClose }) {
  const [busy, setBusy] = useState(null); // channel being acted on

  const allChannels = ['walmart', 'ebay_store2', 'amazon'];
  const channelMap = {};
  (channels || []).forEach(ch => { channelMap[ch.channel] = ch; });

  const linked   = allChannels.filter(c => channelMap[c]?.status === 'active');
  const unlinked = allChannels.filter(c => channelMap[c] && channelMap[c].status !== 'active');
  const notListed = allChannels.filter(c => !channelMap[c]);

  async function handleUnlink(channel) {
    const label = CHANNEL_LABELS[channel] || channel;
    if (!window.confirm(`Unlink ${sku} from ${label}?`)) return;
    setBusy(channel);
    try {
      await api.delete(`/products/${encodeURIComponent(sku)}/channels/${channel}`);
      onUpdate(sku, channel, 'unlinked');
    } catch (err) {
      alert('Unlink failed: ' + (err.response?.data?.error || err.message));
    }
    setBusy(null);
  }

  async function handleRelink(channel) {
    const label = CHANNEL_LABELS[channel] || channel;
    if (!window.confirm(`Re-link ${sku} to ${label}? This will restore the listing and sync inventory.`)) return;
    setBusy(channel);
    try {
      await api.post(`/products/${encodeURIComponent(sku)}/channels/${channel}`);
      onUpdate(sku, channel, 'active');
    } catch (err) {
      alert('Re-link failed: ' + (err.response?.data?.error || err.message));
    }
    setBusy(null);
  }

  function ChannelRow({ channel, status }) {
    const ch = channelMap[channel];
    const label = CHANNEL_LABELS[channel] || channel;
    const isActive = status === 'active';
    const isUnlinked = status === 'unlinked' || status === 'ended';
    const isNotListed = status === 'not_listed';

    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 14px', borderRadius: 8, marginBottom: 6,
        background: isActive ? 'rgba(0,212,170,.06)' : 'rgba(122,127,148,.04)',
        border: isActive ? '1px solid rgba(0,212,170,.2)' : '1px solid var(--border)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: isActive ? 'var(--accent2)' : 'var(--muted)' }}>
              {isActive ? '●' : '○'}
            </span>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{label}</span>
            <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 4, fontWeight: 600,
              background: isActive ? 'rgba(0,212,170,.15)' : isNotListed ? 'rgba(122,127,148,.1)' : 'rgba(255,214,10,.12)',
              color: isActive ? 'var(--accent2)' : isNotListed ? 'var(--muted)' : 'var(--warn)' }}>
              {isActive ? 'Linked' : isNotListed ? 'Not Listed' : ch?.status || 'unlinked'}
            </span>
          </div>
          {ch && isActive && (
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--muted)', paddingLeft: 18 }}>
              {ch.quantity_listed != null && <span>Qty: <strong style={{ color: 'var(--text)' }}>{ch.quantity_listed}</strong></span>}
              {ch.listed_price != null && (
                <span>Price: <strong style={{ color: 'var(--text)' }}>${parseFloat(ch.listed_price).toFixed(2)}</strong>
                  {/* While an eBay sale runs, listed_price IS the discounted price. Show the
                     pre-sale price alongside so the gap against master reads as a sale. */}
                  {!!ch.on_sale && (
                    <>
                      {ch.sale_original_price != null && (
                        <span style={{ marginLeft: 6, textDecoration: 'line-through', opacity: .65 }}>
                          ${parseFloat(ch.sale_original_price).toFixed(2)}
                        </span>
                      )}
                      <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, padding: '1px 5px',
                        borderRadius: 3, background: 'rgba(255,214,10,.15)', color: 'var(--warn)' }}>
                        ON SALE
                      </span>
                    </>
                  )}
                </span>
              )}
              {ch.last_synced_at && <span>Synced: {new Date(ch.last_synced_at).toLocaleDateString()}</span>}
            </div>
          )}
        </div>
        <div>
          {isActive && (
            <button onClick={() => handleUnlink(channel)} disabled={busy === channel}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                border: '1px solid var(--danger)', background: 'rgba(255,77,109,.1)',
                color: 'var(--danger)', cursor: 'pointer' }}>
              {busy === channel ? '…' : 'Unlink'}
            </button>
          )}
          {(isUnlinked || isNotListed) && (
            <button onClick={() => handleRelink(channel)} disabled={busy === channel}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6,
                border: '1px solid var(--accent2)', background: 'rgba(0,212,170,.1)',
                color: 'var(--accent2)', cursor: 'pointer' }}>
              {busy === channel ? '…' : 'Re-link'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card" style={{ width: '100%', maxWidth: 480, margin: 16, maxHeight: '80vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Channel Linking</div>
            <div style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--muted)', marginTop: 2 }}>{sku}</div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20,
            color: 'var(--muted)', cursor: 'pointer', padding: '4px 8px' }}>✕</button>
        </div>

        {linked.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent2)', textTransform: 'uppercase',
              letterSpacing: '.05em', marginBottom: 6 }}>Linked</div>
            {linked.map(c => <ChannelRow key={c} channel={c} status="active" />)}
          </>
        )}

        {(unlinked.length > 0 || notListed.length > 0) && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase',
              letterSpacing: '.05em', marginBottom: 6, marginTop: linked.length > 0 ? 16 : 0 }}>Unlinked</div>
            {unlinked.map(c => <ChannelRow key={c} channel={c} status={channelMap[c]?.status || 'unlinked'} />)}
            {notListed.map(c => <ChannelRow key={c} channel={c} status="not_listed" />)}
          </>
        )}

        {linked.length === 0 && unlinked.length === 0 && notListed.length === allChannels.length && (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>
            Not listed on any channels yet
          </div>
        )}
      </div>
    </div>
  );
}

function ItemCard({ p, onEdit, onLocationUpdate, onDelete, onChannelUpdate }) {
  const imgList = parseImageList(p.images);
  const src = imgList[0] ? resizeEbayUrl(imgList[0], 500) : null;
  const isOut = p.quantity === 0;
  const isLow = p.quantity > 0 && p.quantity <= 2;
  const [showChannels, setShowChannels] = useState(false);
  const [pushing, setPushing] = useState(null); // channel currently being push-synced

  // Per-channel price override (Walmart / Amazon). Empty = follow master (Amazon: master × 1.10).
  const [priceEdit, setPriceEdit] = useState(null);   // { channel, value }
  async function saveChannelPrice(channel) {
    const path = channel === 'walmart' ? '/walmart/price/' : '/amazon/price/';
    setPushing(channel);
    try {
      const r = await api.patch(`${path}${encodeURIComponent(p.sku)}`, { price: priceEdit.value.trim() === '' ? null : priceEdit.value });
      if (r.data.pushError) alert('Saved, but the push failed: ' + r.data.pushError);
      onChannelUpdate(p.sku, channel, null, { listed_price: r.data.effective, override: r.data.walmart_price ?? r.data.amazon_price ?? null });
      setPriceEdit(null);
    } catch (err) {
      alert('Price update failed: ' + (err.response?.data?.error || err.message));
    }
    setPushing(null);
  }

  async function pushSync(channel) {
    setPushing(channel);
    try {
      await api.post(`/products/${encodeURIComponent(p.sku)}/channels/${channel}`);
      onChannelUpdate(p.sku, channel, 'active');
    } catch (err) {
      alert('Push failed: ' + (err.response?.data?.error || err.message));
    }
    setPushing(null);
  }

  const CHANNEL_COLORS = {
    ebay_source: { bg: 'rgba(108,99,255,.15)', color: 'var(--accent)',  label: 'EO',      fullLabel: 'eBay Euphoric' },
    ebay_store2: { bg: 'rgba(0,212,170,.12)',  color: 'var(--accent2)', label: 'TNT',     fullLabel: 'eBay TNT' },
    walmart:     { bg: 'rgba(0,120,215,.15)',  color: '#0078d7',        label: 'WM',      fullLabel: 'Walmart' },
    amazon:      { bg: 'rgba(255,153,0,.15)',   color: '#ff9900',        label: 'AMZ',     fullLabel: 'Amazon' },
    shopify:     { bg: 'rgba(150,191,80,.15)', color: '#96bf50',        label: 'Shopify', fullLabel: 'Shopify' },
  };

  const stockClass = isOut ? 'out' : isLow ? 'low' : 'good';
  const stockLabel = isOut ? 'Out of Stock' : isLow ? 'Low Stock' : `${p.quantity} in stock`;
  const dotClass = isOut ? 'red' : isLow ? 'yellow' : 'green';

  return (
    <>
      <div className="inv-card" onClick={() => onEdit(p)}>
        {/* Media wrapper — relative so the ended banner anchors to the image bottom,
           not the whole card (banner was overlapping the Manage button) */}
        <div className="inv-card-media" style={{ position:'relative' }}>
        {/* Image */}
        {src
          ? <img src={src} alt="" className="inv-card-img" />
          : <div className="inv-card-img-placeholder">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{color:'var(--muted)'}}>
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </div>
        }
        {imgList.length > 1 && (
          <div style={{ position:'absolute', top:8, right:8, background:'rgba(0,0,0,.75)', color:'#fff',
            fontSize:11, fontWeight:700, padding:'3px 8px', borderRadius:10, lineHeight:1,
            pointerEvents:'none', zIndex:1 }}>
            +{imgList.length - 1} photos
          </div>
        )}

        {/* Stock badge */}
        <div className={`inv-card-stock-badge ${stockClass}`}>{stockLabel}</div>

        {/* eBay source ended: loud full-width banner when still in stock (was hidden under the +photos badge);
           muted chip when sold out so legitimately-ended listings don't false-alarm */}
        {(p.channels || []).find(ch => ch.channel === 'ebay_source')?.status === 'ended' && (
          p.quantity > 0 ? (
            <div style={{ position:'absolute', left:0, right:0, bottom:0, background:'rgba(255,77,109,.95)',
              color:'#fff', fontSize:11, fontWeight:800, letterSpacing:'.02em', textAlign:'center',
              padding:'4px 6px', lineHeight:1.2, zIndex:3, pointerEvents:'none' }}>⚠ EBAY ENDED — IN STOCK</div>
          ) : (
            <div className="inv-card-stock-badge" style={{ top:'auto', bottom:8, left:'auto', right:8,
              background:'rgba(120,120,130,.85)', color:'#fff', zIndex:3 }}>Ended</div>
          )
        )}
        </div>{/* /inv-card-media */}

        {/* Body */}
        <div className="inv-card-body">
          {/* Price */}
          <div className="inv-card-price">
            {p.price > 0 ? `$${parseFloat(p.price).toFixed(2)}` : '—'}
            {/* eBay markdown/Sale promo live on at least one channel. eBay refuses price
               revisions while a sale runs (error 21919248), so the channel price sitting
               below master is the sale working as intended — not drift to chase. */}
            {(() => {
              const onSale = (p.channels || []).filter(ch => ch.on_sale);
              if (!onSale.length) return null;
              const names = { ebay_source: 'eBay Euphoric', ebay_store2: 'TNT', walmart: 'Walmart', shopify: 'Shopify', amazon: 'Amazon' };
              const tip = onSale.map(ch => {
                const now  = ch.listed_price != null ? `$${parseFloat(ch.listed_price).toFixed(2)}` : '?';
                const was  = ch.sale_original_price != null ? `$${parseFloat(ch.sale_original_price).toFixed(2)}` : '?';
                return `${names[ch.channel] || ch.channel}: now ${now}, was ${was}`;
              }).join('\n');
              return (
                <span title={tip}
                  style={{ marginLeft: 8, fontSize: 10, fontWeight: 800, letterSpacing: '.03em',
                    padding: '2px 6px', borderRadius: 4, verticalAlign: 'middle', cursor: 'help',
                    background: 'rgba(255,214,10,.15)', color: 'var(--warn)' }}>
                  ON SALE
                </span>
              );
            })()}
          </div>

          {/* Title */}
          <div className="inv-card-title">{p.title || 'Untitled'}</div>

          {/* Meta */}
          <div className="inv-card-meta">
            <span><span className={`dot ${dotClass}`}></span> {p.quantity} in stock</span>
            <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
              {p.location || p.sku_bin || '—'}
            </span>
            <span style={{ fontSize: 11 }}>{p.active_channels} active</span>
          </div>

          {/* Channel list (vertical, ResaleOS-style — one row per channel: dot · name · status) */}
          <div className="inv-card-channels" style={{ display:'flex', flexDirection:'column', gap:4, marginTop:8 }}>
            {(() => {
              const order = { ebay_source: 0, ebay_store2: 1, walmart: 2, shopify: 3, amazon: 4 };
              return (p.channels || [])
                .filter(ch => ch.status !== 'unlinked')
                .sort((a, b) => (order[a.channel] ?? 9) - (order[b.channel] ?? 9));
            })().map(ch => {
              const cc = CHANNEL_COLORS[ch.channel] || { color: 'var(--accent)', fullLabel: ch.channel };
              // Status label depends on channel + product qty for nuanced cases:
              //   - TNT (ebay_store2) + pending + qty<2 = "Not eligible · Singles excluded"
              //     (singles policy intentionally skips TNT, so it's not a stuck listing)
              //   - any pending otherwise = "Not synced" (stuck cross-post or stale relist)
              let statusInfo;
              if (ch.status === 'pending' && ch.channel === 'ebay_store2' && (p.quantity || 0) < 2) {
                statusInfo = { text: 'Not eligible · Singles excluded', color: 'var(--muted)' };
              } else if (ch.status === 'pending') {
                statusInfo = { text: 'Not synced', color: '#fbbf24' };
              } else {
                statusInfo = ({
                  active:  { text: 'Live',  color: '#22c55e' },
                  ended:   { text: 'Ended', color: 'var(--muted)' },
                  error:   { text: 'Error', color: 'var(--danger)' },
                })[ch.status] || { text: ch.status || '—', color: 'var(--muted)' };
              }
              const dotFilled = ch.status === 'active';
              return (
                <div key={ch.channel}
                  title={`${cc.fullLabel}: ${ch.status}`}
                  style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, lineHeight:1.4 }}>
                  <span style={{
                    width:8, height:8, borderRadius:'50%',
                    background: dotFilled ? cc.color : 'transparent',
                    border: dotFilled ? 'none' : `1.5px solid ${cc.color}`,
                    flexShrink:0,
                  }}/>
                  <span style={{ color: 'var(--text)', flex:1, opacity: dotFilled ? 1 : 0.6 }}>{cc.fullLabel}</span>
                  {(ch.channel === 'walmart' || ch.channel === 'amazon') && ch.status === 'active' && (
                    priceEdit?.channel === ch.channel ? (
                      <span onClick={e => e.stopPropagation()} style={{ display:'flex', gap:3, alignItems:'center' }}>
                        <input type="number" step="0.01" min="0" autoFocus value={priceEdit.value} placeholder={ch.channel === 'amazon' ? 'auto' : 'master'}
                          onChange={e => setPriceEdit({ channel: ch.channel, value: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter') saveChannelPrice(ch.channel); if (e.key === 'Escape') setPriceEdit(null); }}
                          style={{ width:64, fontSize:11, padding:'1px 4px', textAlign:'right' }} />
                        <button onClick={() => saveChannelPrice(ch.channel)} disabled={pushing === ch.channel}
                          style={{ fontSize:10, padding:'1px 6px', borderRadius:4, border:`1px solid ${cc.color}`, background:'transparent', color:cc.color, cursor:'pointer' }}>OK</button>
                      </span>
                    ) : (
                      <span onClick={e => { e.stopPropagation(); setPriceEdit({ channel: ch.channel, value: (ch.channel === 'walmart' ? p.walmart_price : p.amazon_price) != null ? String(ch.channel === 'walmart' ? p.walmart_price : p.amazon_price) : '' }); }}
                        title={(ch.channel === 'walmart' ? p.walmart_price : p.amazon_price) != null ? 'Fixed price on this channel — click to change, clear to follow master' : 'Click to set a separate price for this channel'}
                        style={{ fontSize:11, cursor:'pointer', borderBottom:'1px dashed var(--muted)',
                          color: (ch.channel === 'walmart' ? p.walmart_price : p.amazon_price) != null ? 'var(--warn)' : 'var(--muted)' }}>
                        {ch.listed_price != null ? `$${parseFloat(ch.listed_price).toFixed(2)}` : '$—'}{(ch.channel === 'walmart' ? p.walmart_price : p.amazon_price) != null ? ' ✎' : ''}
                      </span>
                    )
                  )}
                  <span style={{ color: statusInfo.color, fontSize:11, fontWeight:600 }}>{statusInfo.text}</span>
                  {(() => {
                    // Recovery action on the card. Only shown when it can actually
                    // succeed, since TNT create/relist goes through mirrorToStore2:
                    //   - skips variation parents (per phase-out policy)
                    //   - needs a live eBay source to mirror from
                    //   - TNT create can also just restore an existing item id (no source needed)
                    //   - Walmart relist isn't an API op, so Ended/Walmart is excluded
                    if (p.is_variation_parent) return null;
                    const isTnt = ch.channel === 'ebay_store2';
                    const isWm  = ch.channel === 'walmart';
                    const sourceActive = (p.channels || []).some(c => c.channel === 'ebay_source' && c.status === 'active');
                    const name = isTnt ? 'TNT' : 'Walmart';
                    let actLabel = null;
                    if (statusInfo.text === 'Not synced') {
                      if (isWm) actLabel = 'Push to Walmart';
                      else if (isTnt && (ch.channel_item_id || sourceActive)) actLabel = 'Push to TNT';
                    } else if (statusInfo.text === 'Ended' && isTnt && sourceActive) {
                      actLabel = 'Relist on TNT';
                    }
                    if (!actLabel) return null;
                    return (
                      <button
                        onClick={e => { e.stopPropagation(); pushSync(ch.channel); }}
                        disabled={pushing === ch.channel}
                        title={`${actLabel} (uses the live eBay source listing)`}
                        style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:5,
                          border:`1px solid ${cc.color}`, background:'rgba(0,212,170,.10)', color:cc.color,
                          cursor: pushing === ch.channel ? 'wait' : 'pointer', whiteSpace:'nowrap' }}>
                        {pushing === ch.channel ? 'Working…' : actLabel}
                      </button>
                    );
                  })()}
                </div>
              );
            })}
            <button onClick={e => { e.stopPropagation(); setShowChannels(true); }}
              style={{ alignSelf:'flex-end', marginTop:2, fontSize:11, padding:'3px 10px', borderRadius:6,
                background:'rgba(108,99,255,.08)', color:'var(--accent)',
                border:'1px dashed var(--border)', cursor:'pointer' }}>
              Manage
            </button>
          </div>
        </div>
      </div>
      {showChannels && (
        <ChannelModal sku={p.sku} channels={p.channels}
          onUpdate={onChannelUpdate}
          onClose={() => setShowChannels(false)} />
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Inventory() {
  const [products, setProducts] = useState([]);
  const [total, setTotal]       = useState(0);
  const [search, setSearch]     = useState('');
  const [filter, setFilter]     = useState('all');
  const [sort, setSort]         = useState('location');
  const [page, setPage]         = useState(1);
  const [editing, setEditing]   = useState(null);
  const LIMIT = 30;

  function load(p = 1, s = search, f = filter, so = sort) {
    const params = new URLSearchParams({ page: p, limit: LIMIT, sort: so });
    if (s) params.set('search', s);
    api.get(`/products?${params}`).then(r => {
      let items = r.data.products;
      if (f === 'low') items = items.filter(i => i.quantity > 0 && i.quantity <= 2);
      if (f === 'out') items = items.filter(i => i.quantity === 0);
      setProducts(items);
      setTotal(r.data.total);
    });
  }

  useEffect(() => { load(1, search, filter, sort); }, [filter, sort]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    load(1, search, filter);
  }

  function updateLocation(sku, loc) {
    setProducts(prev => prev.map(p => p.sku === sku ? { ...p, location: loc } : p));
  }

  function handleSaved(updated) {
    const { oldSku, ...patch } = updated;
    const matchKey = oldSku || updated.sku;
    setProducts(prev => prev.map(p => p.sku === matchKey ? { ...p, ...patch } : p));
    setEditing(null);
  }

  // Patch a row in place without closing the edit panel (handleSaved closes it).
  function handleFieldPatch(sku, patch) {
    setProducts(prev => prev.map(p => p.sku === sku ? { ...p, ...patch } : p));
  }

  function handleDelete(sku) {
    setProducts(prev => prev.filter(p => p.sku !== sku));
    setTotal(prev => prev - 1);
  }

  function handleChannelUpdate(sku, channel, newStatus, extra) {
    setProducts(prev => prev.map(p => {
      if (p.sku !== sku) return p;
      // Price override save: patch the channel's listed price + the product's override field, keep status.
      if (extra) {
        const key = channel === 'walmart' ? 'walmart_price' : 'amazon_price';
        return { ...p, [key]: extra.override,
          channels: (p.channels || []).map(ch => ch.channel === channel ? { ...ch, listed_price: extra.listed_price } : ch) };
      }
      const existing = (p.channels || []).find(ch => ch.channel === channel);
      if (existing) {
        return { ...p, channels: p.channels.map(ch => ch.channel === channel ? { ...ch, status: newStatus } : ch) };
      }
      // Channel wasn't in the list before (not listed → now active)
      return { ...p, channels: [...(p.channels || []), { channel, status: newStatus }] };
    }));
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Inventory <span className="text-muted" style={{ fontSize:14, fontWeight:400 }}>({total})</span></div>
      </div>

      {/* Toolbar */}
      <div className="inv-toolbar">
        <form onSubmit={handleSearch} className="inv-search">
          <span className="inv-search-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search items..." />
          {search && <button type="button" className="btn btn-ghost"
            style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', padding:'4px 8px', fontSize:11 }}
            onClick={() => { setSearch(''); load(1,'',filter); }}>Clear</button>}
        </form>

        <div className="inv-filter-group">
          {[['all','All'],['low','Low Stock'],['out','Out of Stock']].map(([k,l]) => (
            <button key={k} className={`inv-filter-btn ${filter===k?'active':''}`}
              onClick={() => { setFilter(k); setPage(1); }}>{l}</button>
          ))}
        </div>

        <select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}
          className="inv-sort-select">
          <option value="location">By Location</option>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="title">Title A-Z</option>
          <option value="price_desc">Price High-Low</option>
          <option value="price_asc">Price Low-High</option>
          <option value="qty_desc">Qty High-Low</option>
          <option value="qty_asc">Qty Low-High</option>
        </select>

        <span className="inv-results-count">{total} items</span>
      </div>

      {/* Grid */}
      {products.length === 0 ? (
        <div className="empty-state">
          <div style={{ fontSize:48, marginBottom:12, opacity:.5 }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
            </svg>
          </div>
          <div>No products found</div>
        </div>
      ) : (
        <div className="inv-grid">
          {products.map(p => (
            <ItemCard key={p.id} p={p} onEdit={setEditing} onLocationUpdate={updateLocation}
              onDelete={handleDelete} onChannelUpdate={handleChannelUpdate} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div className="pagination">
          <button className="btn btn-ghost" disabled={page===1}
            onClick={() => { setPage(p=>p-1); load(page-1); }}>Prev</button>
          <span className="page-info">Page {page} of {Math.ceil(total/LIMIT)}</span>
          <button className="btn btn-ghost" disabled={page>=Math.ceil(total/LIMIT)}
            onClick={() => { setPage(p=>p+1); load(page+1); }}>Next</button>
        </div>
      )}

      {editing && <EditPanel product={editing} onSaved={handleSaved} onClose={() => setEditing(null)} onFieldPatch={handleFieldPatch} />}
    </div>
  );
}
